import { ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Section } from "../components/ui/Section";
import { getCheckoutStatus } from "../lib/testApi";

type CheckoutViewState =
  | { status: "missing" }
  | { status: "loading"; attempt: number }
  | { status: "paid"; reportUrl: string }
  | { status: "pending_timeout" }
  | { status: "not_found" }
  | { status: "error" };

export function CheckoutReturnPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [viewState, setViewState] = useState<CheckoutViewState>(sessionId ? { status: "loading", attempt: 0 } : { status: "missing" });

  useEffect(() => {
    if (!sessionId) return;
    const checkoutSessionId = sessionId;
    let cancelled = false;
    let timeoutId: number | undefined;

    async function poll(attempt: number) {
      try {
        const result = await getCheckoutStatus(checkoutSessionId);
        if (cancelled) return;

        if (result.status === "paid") {
          setViewState({ status: "paid", reportUrl: result.reportUrl });
          return;
        }

        if (result.status === "not_found") {
          setViewState({ status: "not_found" });
          return;
        }

        if (attempt >= 15) {
          setViewState({ status: "pending_timeout" });
          return;
        }

        setViewState({ status: "loading", attempt: attempt + 1 });
        timeoutId = window.setTimeout(() => poll(attempt + 1), 2000);
      } catch {
        if (!cancelled) setViewState({ status: "error" });
      }
    }

    poll(0);

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [sessionId]);

  const title =
    viewState.status === "paid"
      ? "Το Premium Report είναι έτοιμο"
      : viewState.status === "loading"
        ? "Η πληρωμή επιβεβαιώνεται..."
        : "Δεν μπορέσαμε να επιβεβαιώσουμε την πρόσβαση";
  const body =
    viewState.status === "paid"
      ? "Η πληρωμή επιβεβαιώθηκε και η πρόσβαση στο report ενεργοποιήθηκε."
      : viewState.status === "loading"
        ? "Περιμένουμε την επιβεβαίωση της πληρωμής σου. Αυτό συνήθως παίρνει λίγα δευτερόλεπτα."
        : viewState.status === "missing"
          ? "Λείπει το checkout session από το URL."
          : viewState.status === "pending_timeout"
            ? "Η πληρωμή μπορεί να χρειάζεται λίγο ακόμη χρόνο για να επιβεβαιωθεί. Κάνε ανανέωση σε λίγο ή επικοινώνησε μαζί μας."
            : "Δεν βρέθηκε ενεργό checkout session. Αν ολοκλήρωσες πληρωμή, επικοινώνησε μαζί μας.";

  return (
    <Section className="py-12 sm:py-16">
      <Card className="mx-auto max-w-2xl">
        <ShieldCheck className="text-cyan-200" size={32} />
        <h1 className="mt-4 text-3xl font-bold text-white">{title}</h1>
        <p className="mt-3 leading-7 text-slate-300">{body}</p>
        {viewState.status === "paid" ? (
          <Button href={viewState.reportUrl} className="mt-7">
            Άνοιγμα Premium Report
          </Button>
        ) : (
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Button href="/test">Νέο test</Button>
            <Button href="/contact" variant="secondary">Επικοινωνία</Button>
          </div>
        )}
      </Card>
    </Section>
  );
}
